import { db } from '../lib/firebase';
import {
    collection,
    addDoc,
    query,
    where,
    getDocs,
    serverTimestamp,
    orderBy,
    doc,
    getDoc,
    updateDoc,
    deleteDoc
} from 'firebase/firestore';

/**
 * Service for managing Nodes (Workspaces) and Sessions (Chats).
 */
export const NodeService = {

    /**
     * Creates a new Generic Node.
     * @param {string} userId - Owner UID
     * @param {string} title - Node Title
     * @param {string|null} parentId - Parent Node ID (Optional)
     * @param {Object} [initialSettings] - Optional settings overrides
     * @returns {Promise<string>} - The ID of the new Node
     */
    createNode: async (userId, title, parentId = null, initialSettings = {}, initialMembers = []) => {
        try {
            const defaultSettings = {
                expertId: null,
                modelConfigId: null,
                customInstructions: "",
                desktopEnabled: false,
                targetDeviceId: null,
                path: null
            };

            const nodeData = {
                title,
                ownerId: userId,
                parentId,
                members: Array.from(new Set([userId, ...initialMembers])), // Ensure owner is always a member
                sharedWith: initialMembers, // Initially shared with these users
                admins: [userId], // Default admin is owner
                settings: { ...defaultSettings, ...initialSettings },
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            const nodeRef = await addDoc(collection(db, 'nodes'), nodeData);
            const nodeId = nodeRef.id;

            return nodeId;
        } catch (error) {
            console.error("Error creating node:", error);
            throw error;
        }
    },

    /**
     * Fetches all nodes where the user is a member.
     * @param {string} userId 
     * @returns {Promise<Array>}
     */
    getUserNodes: async (userId) => {
        try {
            const q = query(
                collection(db, 'nodes'),
                where('members', 'array-contains', userId),
                orderBy('updatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error fetching user nodes:", error);
            return [];
        }
    },

    /**
     * Fetches a single Node by ID.
     * @param {string} nodeId 
     * @returns {Promise<Object>}
     */
    getNode: async (nodeId) => {
        try {
            const docRef = doc(db, 'nodes', nodeId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() };
            }
            return null;
        } catch (error) {
            console.error("Error fetching node:", error);
            return null;
        }
    },

    /**
     * Creates a new Session (Chat) within a Node or globally.
     * @param {string} userId - Creator UID
     * @param {string|null} nodeId - Null for standalone chats 
     * @param {string} title 
     * @param {Object} [initialSettings={}] - Optional settings
     * @returns {Promise<string>} - Session ID
     */
    createSession: async (userId, nodeId, title, initialSettings = {}) => {
        try {
            const sessionData = {
                nodeId: nodeId || null, // null for standalone chats
                title,
                type: 'chat',
                createdBy: userId,
                participants: [userId], // Crucial for querying standalone chats
                createdAt: serverTimestamp(),
                lastActive: serverTimestamp(),
                ...initialSettings // Merge any passed settings like desktopEnabled
            };

            const sessionRef = await addDoc(collection(db, 'sessions'), sessionData);
            return sessionRef.id;
        } catch (error) {
            console.error("Error creating session:", error);
            throw error;
        }
    },

    /**
     * Fetches all sessions for a specific Node.
     * @param {string} nodeId 
     * @returns {Promise<Array>}
     */
    getNodeSessions: async (nodeId,userId) => {
        try {
            const q = query(
                collection(db, 'sessions'),
                where('nodeId', '==', nodeId),
                where('participants', 'array-contains', userId),
                orderBy('lastActive', 'desc')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error fetching node sessions:", error);
            return [];
        }
    },

    /**
     * Deletes a session.
     * @param {string} sessionId
     */
    deleteSession: async (sessionId) => {
        try {
            await deleteDoc(doc(db, 'sessions', sessionId));
        } catch (error) {
            console.error("Error deleting session:", error);
            throw error;
        }
    },

    /**
     * Fetches child nodes (Sub-Spaces) for a given parent.
     * @param {string} parentId 
     * @returns {Promise<Array>}
     */
    getChildNodes: async (parentId,userId) => {
        try {
            const q = query(
                collection(db, 'nodes'),
                where('parentId', '==', parentId),
                where('members', 'array-contains', userId),
                orderBy('updatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error fetching child nodes:", error);
            return [];
        }
    },

    /**
     * Updates a node's settings (e.g. expert, context).
     * @param {string} nodeId 
     * @param {Object} newSettings 
     */
    updateNodeSettings: async (nodeId, newSettings) => {
        try {
            const nodeRef = doc(db, 'nodes', nodeId);
            await updateDoc(nodeRef, {
                settings: newSettings,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error updating node settings:", error);
            throw error;
        }
    },

    /**
     * Updates the members list of a node.
     * @param {string} nodeId 
     * @param {Array<string>} memberIds 
     */
    updateNodeMembers: async (nodeId, memberIds) => {
        try {
            const nodeRef = doc(db, 'nodes', nodeId);
            const nodeSnap = await getDoc(nodeRef);
            const ownerId = nodeSnap.data()?.ownerId;

            await updateDoc(nodeRef, {
                members: memberIds,
                sharedWith: memberIds.filter(id => id !== ownerId),
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error updating node members:", error);
            throw error;
        }
    },

    /**
     * Updates the admins list of a node.
     * @param {string} nodeId 
     * @param {Array<string>} adminIds 
     */
    updateNodeAdmins: async (nodeId, adminIds) => {
        try {
            const nodeRef = doc(db, 'nodes', nodeId);
            await updateDoc(nodeRef, {
                admins: adminIds,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error updating node admins:", error);
            throw error;
        }
    },

    /**
     * Helper to recursively fetch parent nodes up to root.
     * @param {string} nodeId 
     * @returns {Promise<Array>} Ordered [Root, Parent, Child]
     */
    getNodeAncestry: async (nodeId) => {
        const chain = [];
        let currentId = nodeId;
        try {
            for (let i = 0; i < 5; i++) {
                if (!currentId) break;
                const docRef = doc(db, 'nodes', currentId);
                const snap = await getDoc(docRef);
                if (!snap.exists()) break;
                const node = { id: snap.id, ...snap.data() };
                chain.unshift(node);
                currentId = node.parentId;
            }
            return chain;
        } catch (error) {
            console.error(error);
            return [];
        }
    },

    /**
     * Updates the title of a node.
     * @param {string} nodeId
     * @param {string} newTitle
     */
    updateNodeTitle: async (nodeId, newTitle) => {
        try {
            const nodeRef = doc(db, 'nodes', nodeId);
            await updateDoc(nodeRef, {
                title: newTitle,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error updating node title:", error);
            throw error;
        }
    },

    /**
     * Deletes a node and all of its sessions.
     * Does NOT recursively delete child nodes (they become orphans or should be handled by caller).
     * @param {string} nodeId
     */
    deleteNode: async (nodeId) => {
        try {
            // Delete all sessions belonging to this node
            const sessionsQuery = query(
                collection(db, 'sessions'),
                where('nodeId', '==', nodeId)
            );
            const sessionsSnap = await getDocs(sessionsQuery);
            const deletePromises = sessionsSnap.docs.map(d => deleteDoc(d.ref));
            await Promise.all(deletePromises);

            // Delete the node itself
            await deleteDoc(doc(db, 'nodes', nodeId));
        } catch (error) {
            console.error("Error deleting node:", error);
            throw error;
        }
    }
};
